import Input from "components/input";
import { Label } from "components/label"
import RadioButton from "components/radioButton";
import Select from "components/select";
import { IAttendantTemplate, IAttendantTemplateInputItem, InputType } from "interfaces"
import { get } from "lodash";
import React from "react"
import { Controller } from "react-hook-form";
import { PHONE_CODE } from "utills/constants";

export const AttendantTemplate = ({register, control, errors, template, isShowLegalField} : {template: IAttendantTemplate, register: any, control: any, errors: any, isShowLegalField: (data: any) => boolean;}) => {
  const stringToOptions = (valueString: string, optionsArray: any[]) => {
    if (!valueString) return [];
    
    const values = valueString.split(',').filter(val => val.trim() !== '');
    return values.map(val => {
      const matchingOption = optionsArray.find(option => option.value === val);
      return matchingOption || { value: val, label: val };
    });
  };

  const optionsToString = (selectedOptions: any[]) => {
    if (!selectedOptions || selectedOptions.length === 0) return '';
    return selectedOptions.map((option: {label: string, value: string}) => option.value).join(',');
  };

  const _renderInputField = (field: IAttendantTemplateInputItem) => {
    switch (field.type) {
      case InputType.CHECKBOX:
        return (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { onChange, value, ref } }) => (
              <Input
                type="checkbox"
                label=""
                id={field.key}
                checked={(value === "1" || value === true)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const isChecked = e.target.checked;
                  const isBooleanField = (field?.key ?? "").startsWith("is_");
                  if (isChecked) {
                    onChange(isBooleanField ? true : "1");
                  } else {
                    onChange(isBooleanField ? false : "0");
                  }
                }
                }    
              />
            )}
          />
        );
      case InputType.MULTI_SELECT: {
        const optionArr = (field.option ?? []).map((opt: {Key: string, Value: any}[]) => ({
          value: get(opt[0], "Value", ""),
          label: get(opt[1], "Value[0].Value", ""),
        }))
        return (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key={field.key}
                  inputRef={ref}
                  options={optionArr}
                  isMulti
                  id={field.key}
                  placeholder={field.label_en}
                  value={stringToOptions(value, optionArr)}
                  onChange={(selectedOptions: any[]) => {
                    onChange(optionsToString(selectedOptions));
                  }}
                  error={get(errors, field.key, "")}
                />
              );
            }}
          />
        )
      }
      case InputType.SELECT: {
        const optionArr = Object.keys(field.option ?? {}).map(key => ({
          value: key,
          label: field.option[key].label_en,
        }));
        return (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { onChange, value = "", ref } }) => (
              <Select 
                inputRef={ref}
                options={optionArr}
                id={field.key}
                placeholder={field.label_en}
                isClearable={!field.require}
                value={optionArr.find((selected: {label: string; value: string}) => selected.value === value)}
                onChange={(selected: { label: string; value: string }, {action} : any) => {
                  if (action === "clear") {
                    onChange("");
                  } else {
                    onChange(selected?.value);
                  }
                }}
                error={get(errors, field.key, "")}
              />
            )}
          />
        )
      }
      case InputType.RADIO_BUTTON: {
        const optionArr = (field.option ?? []).map((opt: {Key: string, Value: any}[]) => ({
          value: get(opt[0], "Value", ""),
          label: get(opt[1], "Value[0].Value", ""),
        }));
        return (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <div className="flex flex-col gap-y-3">
                  {(optionArr ?? []).map((opt: {value: string, label: string}) => (
                    <RadioButton
                      checked={opt.value === value}
                      onChange={() => {
                        onChange(opt.value);
                      }}
                      item={{
                        id: opt.value,
                        title: opt.label,
                      }}
                      key={opt.value}
                    />
                  ))}
                </div>
              );
            }}
          />
        )
      }
      case InputType.DATETIME:
        return (
          <Input
            id={field.key}
            type="datetime-local"
            placeholder={field.label_en}
            error={get(errors, field.key, "")}
            {...register(field.key)}
          />
        );
      case InputType.TEXT: {
        if (field.key.includes("phone") && field.validation === "phone") {
          return (
            <div className="flex">
              <Controller
                control={control}
                name={`${field.key}_area_code`}
                render={({ field: { onChange, value = "", ref } }) => {
                  const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                  return (
                    <Select 
                      ref={ref}
                      className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                      options={areaPhoneCodeArr} 
                      id={`${field.key}_area_code`}
                      value={areaPhoneCodeArr.find(c => c.value === value)}
                      onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                      error={get(errors, `${field.key}_area_code`, "")}
                    />
                  )
                }}
              />
              <Input
                id={field.key}
                type={field.type}
                placeholder={field.label_en}
                className={`${get(errors, field.key, "") ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                error={get(errors, field.key, "")}
                {...register(field.key)}
              />
            </div>
          )
        }
        return (
          <Input
            id={field.key}
            type={field.type}
            placeholder={field.label_en}
            error={get(errors, field.key, "")}
            {...register(field.key)}
          />
        )
      }
      default:
        return (
          <Input
            id={field.key}
            type={field.type}
            placeholder={field.label_en}
            error={get(errors, field.key, "")}
            {...register(field.key)}
          />
        )
    }
  }

  return (
    <div className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm items-start sm:px-6 grid-cols-12 lg:gap-x-6" style={{wordBreak: "break-word"}}>
      {Object.keys(template ?? {}).map(key => (
        <React.Fragment key={key}>
          <div className="col-span-full">
            <p className="text-lg font-medium">
              {template[key].label_en}
            </p>
          </div>
          {(template[key].fields ?? []).map(field => isShowLegalField(field) ? (
            <div className="col-span-full sm:col-span-6" key={field.key}>
              <Label 
                id={field.key}
                name={field.label_en}
                required={field.require}
              />
              {_renderInputField(field)}
              {get(errors, field.key, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `${field.key}.message`, "") as string}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null)}
        </React.Fragment>
      ))}
    </div>
  )
}